import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, getDocs, doc, getDoc, query, where,
    addDoc, updateDoc, arrayUnion, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { Box, Typography, Chip, Button, CircularProgress, Snackbar, Alert, Divider, IconButton } from '@mui/material';
import { ArrowBack, ArrowForwardIos, ArrowBackIos, CheckCircle } from '@mui/icons-material';
import barberFallback from '../../images/barber-fallback.png';
import { colors, typography, radius, btnPrimary, glassCard } from '../../theme';

// ─── helpers ──────────────────────────────────────────────────────────────────
const DAYS     = ['SU','MO','TU','WE','TH','FR','SA'];
const MONTHS   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function generateSlots(openTime = '09:00', closeTime = '18:00', duration = 30) {
    const slots = [];
    const [oh, om] = openTime.split(':').map(Number);
    const [ch, cm] = closeTime.split(':').map(Number);
    let cur = oh * 60 + om;
    const end = ch * 60 + cm;
    while (cur + duration <= end) {
        const h = Math.floor(cur / 60), m = cur % 60;
        const hh = h % 12 === 0 ? 12 : h % 12;
        const mm = m.toString().padStart(2, '0');
        const period = h < 12 ? 'AM' : 'PM';
        slots.push({ label: `${hh.toString().padStart(2,'0')}.${mm} ${period}`, minutes: cur });
        cur += duration;
    }
    return slots;
}

// Build a full calendar month grid — returns array of {date, isCurrentMonth}
function buildMonthGrid(year, month) {
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const cells = [];
    // Previous month fill
    for (let i = firstDay - 1; i >= 0; i--) {
        cells.push({ date: new Date(year, month - 1, daysInPrevMonth - i), isCurrentMonth: false });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    // Next month fill to complete rows
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
        cells.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
    }
    return cells;
}

// ─── Step Header ──────────────────────────────────────────────────────────────
const StepHeader = ({ number, title, done }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {done ? (
            <CheckCircle sx={{ color: colors.success, fontSize: 22 }} />
        ) : (
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#322920', lineHeight: 1 }}>{number}</Typography>
            </Box>
        )}
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 600, color: done ? colors.textPrimary : colors.textPrimary }}>
            {title}
        </Typography>
        {done && <Typography sx={{ fontSize: 11, color: colors.success, ml: 'auto' }}>✓ Done</Typography>}
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const BarberBookingPage = () => {
    const { shopId, barberId } = useParams();
    const navigate = useNavigate();

    const [barber,          setBarber]          = useState(null);
    const [shop,            setShop]            = useState(null);
    const [loading,         setLoading]         = useState(true);
    const [authReady,       setAuthReady]       = useState(false);
    const [selectedDate,    setSelectedDate]    = useState(new Date());
    const [calMonth,        setCalMonth]        = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
    const [bookedSlots,     setBookedSlots]     = useState([]);
    const [selectedSlot,    setSelectedSlot]    = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [booking,         setBooking]         = useState(false);
    const [toast,           setToast]           = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => { const unsub = onAuthStateChanged(auth, () => setAuthReady(true)); return () => unsub(); }, []);

    useEffect(() => {
        if (!authReady) return;
        const load = async () => {
            try {
                const [barberSnap, shopSnap] = await Promise.all([
                    getDoc(doc(db, 'shops', shopId, 'barbers', barberId)),
                    getDoc(doc(db, 'shops', shopId)),
                ]);
                if (barberSnap.exists()) setBarber({ id: barberSnap.id, ...barberSnap.data() });
                if (shopSnap.exists())   setShop({ id: shopSnap.id, ...shopSnap.data() });
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [authReady, shopId, barberId]);

    const fetchBookedSlots = useCallback(async (date) => {
        if (!auth.currentUser) { setBookedSlots([]); return; }
        try {
            const start = new Date(date); start.setHours(0, 0, 0, 0);
            const end   = new Date(date); end.setHours(23, 59, 59, 999);
            const q = query(collection(db, 'bookings'), where('barberId', '==', barberId), where('scheduledAt', '>=', Timestamp.fromDate(start)), where('scheduledAt', '<=', Timestamp.fromDate(end)), where('status', '!=', 'cancelled'));
            const snap = await getDocs(q);
            setBookedSlots(snap.docs.map(d => d.data().slotMinutes));
        } catch (e) { setBookedSlots([]); }
    }, [barberId]);

    useEffect(() => { if (authReady) fetchBookedSlots(selectedDate); }, [selectedDate, fetchBookedSlots, authReady]);

    const today       = new Date(); today.setHours(0, 0, 0, 0);
    const dayKey      = DAY_KEYS[selectedDate.getDay()];
    const daySchedule = shop?.operatingHours?.[dayKey];
    const isDayOff    = !daySchedule || daySchedule.isOff;
    const allSlots    = isDayOff ? [] : generateSlots(daySchedule?.open, daySchedule?.close, 30);
    const services    = shop?.services?.length > 0 ? shop.services : (barber?.services || []);
    const step1Done   = !!selectedService;
    const step3Done   = !!selectedSlot;

    const calGrid = buildMonthGrid(calMonth.year, calMonth.month);

    const prevMonth = () => setCalMonth(({ year, month }) => month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
    const nextMonth = () => setCalMonth(({ year, month }) => month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });

    const handleConfirm = async () => {
        if (!selectedSlot || !selectedService) { setToast({ open: true, msg: 'Please pick a service and time slot', severity: 'warning' }); return; }
        if (!auth.currentUser) { navigate('/login'); return; }
        setBooking(true);
        try {
            const scheduled = new Date(selectedDate);
            scheduled.setHours(Math.floor(selectedSlot.minutes / 60), selectedSlot.minutes % 60, 0, 0);
            const bookingRef = await addDoc(collection(db, 'bookings'), {
                type: 'appointment', barberId, shopId,
                userId: auth.currentUser.uid,
                service: selectedService.name, price: selectedService.price,
                slotMinutes: selectedSlot.minutes,
                scheduledAt: Timestamp.fromDate(scheduled),
                status: 'pending', paymentStatus: 'unpaid', notes: '',
                createdAt: serverTimestamp(),
            });
            await updateDoc(doc(db, 'shops', shopId, 'barbers', barberId), { waitingList: arrayUnion(bookingRef.id) });
            setToast({ open: true, msg: 'Booking confirmed! 🎉', severity: 'success' });
            setSelectedSlot(null);
            fetchBookedSlots(selectedDate);
        } catch (e) {
            setToast({ open: true, msg: 'Booking failed. Try again.', severity: 'error' });
        }
        setBooking(false);
    };

    if (!authReady || loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: colors.bg }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    if (!barber) return (
        <Box sx={{ p: 4, color: colors.textPrimary, bgcolor: colors.bg, minHeight: '100vh' }}>
            <Typography>Barber not found.</Typography>
            <Button onClick={() => navigate(-1)} sx={{ color: colors.primary, mt: 2 }}>Go Back</Button>
        </Box>
    );

    const summaryPill = { display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: colors.primary, border: `1px solid ${colors.primaryBorder}`, borderRadius: radius.md, px: 1.5, py: 0.5, mb: 1.5 };

    return (
        <Box sx={{ minHeight: '100vh', background: colors.bg, backgroundAttachment: 'fixed', color: colors.textPrimary, pb: 14 }}>

            {/* ── Hero ── */}
            <Box sx={{ position: 'relative', height: 260, overflow: 'hidden' }}>
                <Box component="img" src={barber.image || barberFallback} alt={barber.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', transform: 'scale(1.05)' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.10) 40%, rgba(0,0,0,0.72) 100%)' }} />
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', top: 16, left: 16, backdropFilter: 'blur(12px)', bgcolor: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.50)' } }}>
                    <ArrowBack />
                </IconButton>
                <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, backdropFilter: 'blur(14px)', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3, px: 2, py: 1.5 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 24, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{barber.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, flexWrap: 'wrap' }}>
                        {barber.specialty?.map((s, i) => (
                            <Chip key={i} label={s} size="small" sx={{ backdropFilter: 'blur(8px)', bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', fontSize: 11 }} />
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ── Stats row ── */}
            <Box sx={{ display: 'flex', gap: 2, px: 3, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }}>⭐</Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{barber.rating ?? '—'} Rating</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }}>✂️</Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{barber.experience ?? '—'} yrs exp</Typography>
                </Box>
            </Box>

            <Divider sx={{ bgcolor: colors.textPrimary, mx: 3, mt: 2 }} />

            {/* ── STEP 1: Service ── */}
            <Box sx={{ px: 3, mt: 3 }}>
                <StepHeader number={1} title="Select Service" done={step1Done} />
                {step1Done && (
                    <Box sx={summaryPill}>
                        <CheckCircle sx={{ fontSize: 14, color: colors.textPrimary }} />
                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>{selectedService.name} · RM {selectedService.price} · {selectedService.duration}min</Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: 1.5, pb: 1, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: colors.border, borderRadius: 2 } }}>
                    {services.length === 0 && <Typography sx={{ color: colors.textMuted, fontSize: 13 }}>No services listed</Typography>}
                    {services.map((svc, i) => {
                        const active = selectedService?.name === svc.name;
                        return (
                            <Box key={i} onClick={() => setSelectedService(svc)} sx={{
                                flexShrink: 0, px: 2, py: 1.5, borderRadius: radius.md, minWidth: 110, textAlign: 'center',
                                cursor: 'pointer', transition: 'all .2s',
                                bgcolor: active ? colors.primary : colors.bgCard,
                                border: `1px solid ${colors.textPrimary}`,
                                boxShadow: active ? `0 4px 12px ${colors.primary}44` : 'none',
                                ...(!active && glassCard),
                            }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: active ? '#322920' : colors.textPrimary }}>{svc.name}</Typography>
                                <Typography sx={{ fontSize: 12, color: active ? '#32292099' : colors.textFaint, mt: 0.3 }}>RM {svc.price} · {svc.duration}min</Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Divider sx={{ bgcolor: colors.textPrimary, mx: 3, mt: 3 }} />

            {/* ── STEP 2: Calendar ── */}
            <Box sx={{ px: 3, mt: 3 }}>
                <StepHeader number={2} title="Choose Your Day" done={true} />

                <Box sx={{ ...glassCard, borderRadius: 3, overflow: 'hidden', p: 2 }}>
                    {/* Month header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <IconButton size="small" onClick={prevMonth} sx={{ color: colors.primary, p: 0.5 }}>
                            <ArrowBackIos fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                            {MONTHS[calMonth.month]} {calMonth.year}
                        </Typography>
                        <IconButton size="small" onClick={nextMonth} sx={{ color: colors.primary, p: 0.5 }}>
                            <ArrowForwardIos fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Day headers */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                        {DAYS.map(d => (
                            <Typography key={d} sx={{ fontSize: 11, fontWeight: 700, color: colors.textFaint, textAlign: 'center', py: 0.5 }}>{d}</Typography>
                        ))}
                    </Box>

                    {/* Date cells */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3 }}>
                        {calGrid.map((cell, i) => {
                            const isSelected  = sameDay(cell.date, selectedDate);
                            const isToday     = sameDay(cell.date, today);
                            const isPast      = cell.date < today;
                            const dayK        = DAY_KEYS[cell.date.getDay()];
                            const isOff       = !shop?.operatingHours?.[dayK] || shop?.operatingHours?.[dayK]?.isOff;
                            const disabled    = isPast || isOff || !cell.isCurrentMonth;

                            return (
                                <Box key={i} onClick={() => !disabled && setSelectedDate(cell.date)} sx={{
                                    aspectRatio: '1',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: 2, cursor: disabled ? 'default' : 'pointer',
                                    bgcolor: isSelected ? colors.primary : 'transparent',
                                    border: isToday && !isSelected ? `1px solid ${colors.primary}` : '1px solid transparent',
                                    opacity: disabled ? 0.25 : 1,
                                    transition: 'all .15s',
                                    '&:hover': !disabled ? { bgcolor: isSelected ? colors.primary : colors.primaryDim } : {},
                                }}>
                                    <Typography sx={{
                                        fontSize: 13, fontWeight: isSelected ? 700 : isToday ? 600 : 400,
                                        color: isSelected ? '#322020' : !cell.isCurrentMonth ? colors.textDisabled : colors.textPrimary,
                                        lineHeight: 1,
                                    }}>
                                        {cell.date.getDate()}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Selected date label */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>
                        Selected: <Typography component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                            {selectedDate.toLocaleDateString('en-MY', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                    </Typography>
                    {isDayOff && (
                        <Box sx={{ bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, px: 1, py: 0.2 }}>
                            <Typography sx={{ fontSize: 10, color: colors.error, fontWeight: 600 }}>CLOSED</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Divider sx={{ bgcolor: colors.textPrimary, mx: 3, mt: 3 }} />

            {/* ── STEP 3: Time slots ── */}
            <Box sx={{ px: 3, mt: 3 }}>
                <StepHeader number={3} title="Pick a Time Slot" done={step3Done} />
                {step3Done && (
                    <Box sx={summaryPill}>
                        <CheckCircle sx={{ fontSize: 14, color: colors.textPrimary }} />
                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>{selectedSlot.label} selected</Typography>
                    </Box>
                )}

                {isDayOff ? (
                    <Box sx={{ textAlign: 'center', py: 4, ...glassCard, borderRadius: 3 }}>
                        <Typography sx={{ fontSize: 32 }}>🚫</Typography>
                        <Typography sx={{ mt: 1, fontSize: 14, color: colors.textFaint }}>Closed on this day</Typography>
                    </Box>
                ) : (
                    <Box sx={{ ...glassCard, borderRadius: 3, p: 2 }}>
                        {/* 3-column time slot grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                            {allSlots.map((slot, i) => {
                                const booked   = bookedSlots.includes(slot.minutes);
                                const active   = selectedSlot?.minutes === slot.minutes;

                                return (
                                    <Box key={i} onClick={() => !booked && setSelectedSlot(slot)} sx={{
                                        py: 1, px: 0.5, borderRadius: 2, textAlign: 'center',
                                        cursor: booked ? 'not-allowed' : 'pointer',
                                        transition: 'all .15s',
                                        // ── active: golden, booked: terracotta, free: transparent ──
                                        bgcolor: active ? colors.primary : booked ? '#a5514f' : 'transparent',
                                        border: `1px solid ${active ? colors.primary : booked ? '#a5514f' : colors.border}`,
                                        '&:hover': !booked && !active ? { bgcolor: colors.primaryDim, borderColor: colors.primary } : {},
                                    }}>
                                        {booked && !active ? (
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>BOOKED</Typography>
                                        ) : (
                                            <Typography sx={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? '#322920' : colors.textPrimary }}>
                                                {slot.label}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Legend */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
                            {[
                                { color: 'transparent', border: colors.border,   label: 'Available' },
                                { color: colors.primary, border: colors.primary, label: 'Selected'  },
                                { color: '#a5514f',      border: '#a5514f',      label: 'Booked'    },
                            ].map(({ color, border, label }) => (
                                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, border: `1px solid ${border}` }} />
                                    <Typography sx={{ fontSize: 10, color: colors.textFaint }}>{label}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Confirm bar ── */}
            <Box sx={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                background: colors.bg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                borderTop: `1px solid ${colors.textPrimary}`,
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.3 }}>
                        {[step1Done, true, step3Done].map((done, i) => (
                            <Box key={i} sx={{ width: done ? 20 : 6, height: 5, borderRadius: 3, bgcolor: done ? colors.success : colors.border, transition: 'all .3s' }} />
                        ))}
                    </Box>
                    {selectedSlot && selectedService ? (
                        <>
                            <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>{selectedService.name} · {selectedSlot.label}</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                                {selectedDate.toLocaleDateString('en-MY', { weekday: 'short', month: 'short', day: 'numeric' })} · RM {selectedService.price}
                            </Typography>
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 12, color: colors.textFaint }}>
                            {!step1Done ? 'Select a service first' : !step3Done ? 'Pick a time slot' : ''}
                        </Typography>
                    )}
                </Box>
                <Button variant="contained" onClick={handleConfirm} disabled={booking || !selectedSlot || !selectedService}
                    sx={{ ...btnPrimary, px: 3, fontSize: 14, bgcolor: colors.primary, color: '#322920' }}>
                    {booking ? <CircularProgress size={18} sx={{ color: '#322920' }} /> : 'Confirm Booking'}
                </Button>
            </Box>

            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={toast.severity} sx={{ width: '100%' }}>{toast.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default BarberBookingPage;